<script setup>
import { useFloating } from '@/composables/useFloating.js'
import UiMenuItems from './UiMenuItems.vue'

/**
 * A dropdown. Items: [{label, icon, action, danger, disabled, divider, to, header, hint, keep}] or the
 * default slot — a header names the items under it, a hint stands to the right of a label, an
 * item with `keep` leaves the list open (a step taken more than once).
 * The list floats over the whole page by its button (useFloating, D-152).
 */
const props = defineProps({
  items: { type: Array, default: () => [] },
  align: { type: String, default: 'right' },
  label: { type: String, default: '' },
  // Said on hovering a button of an icon alone: what the menu holds.
  tip: { type: String, default: '' },
  icon: { type: String, default: 'fa-ellipsis' },
  buttonClass: { type: String, default: '' },
  variant: { type: String, default: 'ghost' },
  // Attributes for the list that opens (it floats apart from the button, in the page's body).
  listAttrs: { type: Object, default: () => ({}) },
})
const { open, anchor: root, floating: menu, style, toggle, close } = useFloating({ align: () => props.align })
function run(item) {
  if (item.disabled) return
  if (!item.keep) close()
  item.action?.()
}
defineExpose({ close })
</script>

<template>
  <div ref="root" class="relative inline-block">
    <button
      type="button"
      :class="[buttonClass || 'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium', variant === 'ghost' ? 'text-muted hover:bg-[var(--surface-3)]' : variant === 'outline' ? 'surface border border-default hover:bg-[var(--surface-3)]' : '']"
      :aria-expanded="open"
      :aria-label="label || tip || $t('common.more')"
      :title="!label ? tip || $t('common.more') : undefined"
      @click="toggle"
    >
      <slot name="button"><i :class="['fa-solid', icon]" aria-hidden="true"></i><span v-if="label">{{ label }}</span></slot>
    </button>
    <Teleport to="body">
      <div v-if="open" ref="menu" v-bind="listAttrs" class="surface fixed min-w-52 max-w-[min(24rem,calc(100vw-1rem))] overflow-y-auto rounded-xl border border-default py-1 text-left shadow-xl" :style="style" role="menu">
        <slot :close="close">
          <UiMenuItems :items="items" @run="run" @close="close" />
        </slot>
      </div>
    </Teleport>
  </div>
</template>
